import React from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import StatusProgressDots from './StatusProgressDots';
import {
  Archive, FolderOpen, ChatCircle, UploadSimple, CaretUp, CaretDown,
} from '@phosphor-icons/react';
import { formatRelativeTime, formatDOB } from './dateUtils';
import './referralsTable.scss';

const STATUS_BADGE_MAP = {
  Received: 'received',
  'Contacting Patient': 'contacting',
  'Waiting on Info': 'waiting',
  Scheduled: 'scheduled',
  'Seen/Visit Held': 'completed',
  'Canceled/Declined': 'canceled',
};

const isRecentActivity = (dateString) => {
  if (!dateString) return false;
  const activityDate = new Date(dateString);
  const now = new Date();
  return (now - activityDate) < 24 * 60 * 60 * 1000;
};

const getEmptyStateMessage = (activeStatFilter, filters) => {
  const hasPatientSearch = filters?.patient?.trim();
  const statusArr = Array.isArray(activeStatFilter) ? activeStatFilter : [];
  const filterStatusArr = Array.isArray(filters?.status) ? filters.status : [];
  const hasStatusFilter = statusArr.length > 0 || filterStatusArr.length > 0;
  const hasOtherFilters = filters?.provider || filters?.dob || filters?.date_from || filters?.date_to;
  const hasAnyFilter = hasPatientSearch || hasStatusFilter || hasOtherFilters;

  if (hasPatientSearch && !hasStatusFilter && !hasOtherFilters) {
    return { title: `No results for '${filters.patient.trim()}'`, subtitle: 'Try a different search term', hasFilters: true };
  }
  if (hasStatusFilter && !hasPatientSearch && !hasOtherFilters) {
    const names = statusArr.length > 0 ? statusArr : filterStatusArr;
    const label = names.length === 1 ? names[0] : 'matching';
    return { title: `No ${label} referrals`, subtitle: 'Try selecting a different status', hasFilters: true };
  }
  if (hasAnyFilter) {
    return { title: 'No referrals match your filters', subtitle: 'Try adjusting or clearing your filters', hasFilters: true };
  }
  return { title: 'No referrals yet', subtitle: 'Referrals will appear here once created', hasFilters: false };
};

const SortIndicator = ({ sortBy: activeSortBy, sortDirection: dir, column }) => {
  if (activeSortBy !== column) return null;
  return dir === 'asc'
    ? <CaretUp size={12} weight="bold" className="referrals-table__sort-icon" />
    : <CaretDown size={12} weight="bold" className="referrals-table__sort-icon" />;
};

SortIndicator.propTypes = {
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  column: PropTypes.string.isRequired,
};

SortIndicator.defaultProps = {
  sortBy: null,
  sortDirection: 'asc',
};

const ReferralsTable = ({
  referrals, onReferralClick, onQuickAction, selectedId,
  sortBy, sortDirection, onSort, activeStatFilter, filters, onClearFilters,
}) => {
  if (referrals.length === 0) {
    const emptyState = getEmptyStateMessage(activeStatFilter, filters);
    return (
      <div className="referrals-table-empty">
        <FolderOpen size={40} weight="light" className="referrals-table-empty__icon" />
        <Typography variant="text" size="small" className="referrals-table-empty__title">
          {emptyState.title}
        </Typography>
        <Typography variant="text" size="xsmall" className="referrals-table-empty__subtitle">
          {emptyState.subtitle}
        </Typography>
        {emptyState.hasFilters && onClearFilters && (
          <button type="button" className="referrals-table-empty__clear-btn" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="referrals-table-wrapper">
      <table className="referrals-table">
        <thead>
          <tr>
            <th
              className="referrals-table__th--sortable"
              onClick={() => onSort && onSort('patient_name')}
            >
              Patient
              <SortIndicator sortBy={sortBy} sortDirection={sortDirection} column="patient_name" />
            </th>
            <th>Practice / Provider</th>
            <th>Status</th>
            <th
              className="referrals-table__th--sortable"
              onClick={() => onSort && onSort('last_activity_at')}
            >
              Last Activity
              <SortIndicator sortBy={sortBy} sortDirection={sortDirection} column="last_activity_at" />
            </th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((referral) => {
            const badgeKey = STATUS_BADGE_MAP[referral.status] || 'received';
            const activity = formatRelativeTime(referral.last_activity_at);

            return (
              <tr
                key={referral.id}
                className={`referrals-table__row ${selectedId === referral.id ? 'referrals-table__row--selected' : ''} ${referral.archived ? 'referrals-table__row--archived' : ''}`}
                onClick={() => onReferralClick(referral)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onReferralClick(referral);
                  }
                }}
              >
                <td className="referrals-table__patient">
                  <div className="referrals-table__patient-name-row">
                    <Typography variant="text" bold>
                      {referral.patient_name}
                    </Typography>
                    {referral.unread_messages_count > 0 && !referral.archived && (
                      <span className="referrals-table__unread-dot" />
                    )}
                  </div>
                  <Typography variant="text" size="xsmall" className="referrals-table__dob">
                    {formatDOB(referral.patient_dob)}
                  </Typography>
                </td>
                <td className="referrals-table__provider">
                  <Typography variant="text">
                    {referral.referring_organization}
                  </Typography>
                  <Typography variant="text" size="xsmall" className="referrals-table__provider-name">
                    {referral.referring_provider}
                  </Typography>
                </td>
                <td className="referrals-table__status">
                  <div className="referrals-table__status-content">
                    <span className={`referrals-table__status-badge referrals-table__status-badge--${badgeKey}`}>
                      {referral.status}
                    </span>
                    <StatusProgressDots status={referral.status} hasFinalStep={referral.has_seen_step} statusDates={referral.status_dates} />
                  </div>
                </td>
                <td className="referrals-table__activity">
                  <div className="referrals-table__activity-row">
                    <span className="referrals-table__activity-relative">
                      {activity.relative}
                    </span>
                    {isRecentActivity(referral.last_activity_at) && (
                      <span className="referrals-table__updated-dot" title="Updated in the last 24 hours" />
                    )}
                    {!referral.archived && (
                    <div className="referrals-table__quick-actions">
                      <button
                        type="button"
                        className="referrals-table__quick-btn"
                        title="Messages"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onQuickAction) onQuickAction(referral, 'messages');
                        }}
                      >
                        <ChatCircle size={15} weight="bold" />
                      </button>
                      <button
                        type="button"
                        className="referrals-table__quick-btn"
                        title="Upload Document"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onQuickAction) onQuickAction(referral, 'documents');
                        }}
                      >
                        <UploadSimple size={15} weight="bold" />
                      </button>
                    </div>
                    )}
                  </div>
                  <span className="referrals-table__activity-exact">
                    {activity.exact}
                  </span>
                </td>
                {referral.archived && (
                  <td className="referrals-table__badge-cell">
                    <span className="referrals-table__archived-badge">
                      <Archive size={12} /> Archived
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

ReferralsTable.propTypes = {
  referrals: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    patient_name: PropTypes.string.isRequired,
    patient_dob: PropTypes.string,
    referring_organization: PropTypes.string,
    referring_provider: PropTypes.string,
    status: PropTypes.string.isRequired,
    auth_status: PropTypes.string,
    last_activity_at: PropTypes.string,
    unread_messages_count: PropTypes.number,
    archived: PropTypes.bool,
    status_dates: PropTypes.object,
  })).isRequired,
  onReferralClick: PropTypes.func.isRequired,
  onQuickAction: PropTypes.func,
  selectedId: PropTypes.number,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  activeStatFilter: PropTypes.arrayOf(PropTypes.string),
  filters: PropTypes.object,
  onClearFilters: PropTypes.func,
};

ReferralsTable.defaultProps = {
  onQuickAction: null,
  selectedId: null,
  sortBy: null,
  sortDirection: 'asc',
  onSort: null,
  activeStatFilter: [],
  filters: null,
  onClearFilters: null,
};

export default ReferralsTable;
